import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Banknote, Coins, Lightbulb, Wallet } from 'lucide-react';
import { isFiniteNumber, normalizeCents as normalizeCentsOrNull, formatMoneyDisplay } from '../../utils/moneyFormatters';
import { getMoneyStyles, getDenominationDisplay, isBill, EURO_BILLS_CENTS, EURO_COINS_CENTS } from './moneyStyles';
// ── Types ────────────────────────────────────────────────────
export interface MoneyBreakdown {
  bills: {
    value: number;
    count: number;
  }[];
  coins: {
    value: number;
    count: number;
  }[];
}
export interface MoneyVisualProps {
  /** Amount in cents */
  amount: number;
  /** Explicit breakdown (overrides auto-breakdown) */
  breakdown?: MoneyBreakdown;
  showCoins?: boolean;
  showBills?: boolean;
  interactive?: boolean;
  label?: string;
  /** 'stimulus' = compact context visual (no answer leaking), 'support' = full educational panel */
  mode?: 'stimulus' | 'support';
  /** Group identical bills/coins together in stimulus mode */
  groupBy?: 'value' | 'none';
}
// ── Helpers ──────────────────────────────────────────────────
function normalizeCents(amount: number): number {
  return normalizeCentsOrNull(amount) ?? 0;
}
function normalizeBreakdown(breakdown?: MoneyBreakdown): MoneyBreakdown | null {
  if (!breakdown) return null;
  const bills = Array.isArray(breakdown.bills) ? breakdown.bills : [];
  const coins = Array.isArray(breakdown.coins) ? breakdown.coins : [];
  const safeBills = bills.filter(b => isFiniteNumber(b?.value) && isFiniteNumber(b?.count)).map(b => ({
    value: Math.round(b.value),
    count: Math.max(0, Math.round(b.count))
  })).filter(b => b.count > 0);
  const safeCoins = coins.filter(c => isFiniteNumber(c?.value) && isFiniteNumber(c?.count)).map(c => ({
    value: Math.round(c.value),
    count: Math.max(0, Math.round(c.count))
  })).filter(c => c.count > 0);
  return {
    bills: safeBills,
    coins: safeCoins
  };
}
function autoBreakdown(cents: number): MoneyBreakdown {
  let remaining = cents;
  const bills: {
    value: number;
    count: number;
  }[] = [];
  const coins: {
    value: number;
    count: number;
  }[] = [];
  for (const billCents of EURO_BILLS_CENTS) {
    const billEuro = billCents / 100;
    const count = Math.floor(remaining / billCents);
    if (count > 0) {
      bills.push({
        value: billEuro,
        count
      });
      remaining -= count * billCents;
    }
  }
  for (const coinValue of EURO_COINS_CENTS) {
    const count = Math.floor(remaining / coinValue);
    if (count > 0) {
      coins.push({
        value: coinValue,
        count
      });
      remaining -= count * coinValue;
    }
  }
  return {
    bills,
    coins
  };
}
function formatMoney(cents: number): string {
  return formatMoneyDisplay(normalizeCents(cents));
}
// ── Token Renderer ───────────────────────────────────────────
function MoneyToken({
  cents,
  index,
  compact = false
}: {
  cents: number;
  index: number;
  compact?: boolean;
}) {
  const styles = getMoneyStyles(cents);
  const label = getDenominationDisplay(cents);
  const scaleClass = compact ? 'scale-75 origin-center' : '';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: index * 0.04,
    duration: 0.25
  }} className={`${styles.container} ${styles.bg} ${styles.border} flex items-center justify-center relative overflow-hidden ${scaleClass}`} aria-label={label} data-id="element-12119">
      {styles.inner && <div className={`absolute inset-1 rounded ${styles.inner}`} data-id="element-12120" />}
      <div className="relative z-10 flex items-center justify-center" data-id="element-12121">
        <span className={styles.text} data-id="element-12122">{label}</span>
      </div>
    </motion.div>;
}
// ── Stimulus Mode ────────────────────────────────────────────
// Shows coins/bills as visual context WITHOUT revealing the total or breakdown.
// ONLY uses explicit breakdown. Never auto-generates.
function StimulusMode({
  breakdown,
  showBills,
  showCoins,
  label,
  groupBy = 'value'
}: {
  breakdown: MoneyBreakdown | null;
  showBills: boolean;
  showCoins: boolean;
  label?: string;
  groupBy?: 'value' | 'none';
}) {
  if (!breakdown) {
    if (!label) return null;
    return <div className="w-full max-w-sm mx-auto" data-id="element-12123">
        <div className="bg-white rounded-xl p-3 border border-slate-200 flex items-center justify-center gap-2 shadow-sm" data-id="element-12124">
          <Wallet className="w-4 h-4 text-slate-400" data-id="element-12125" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-12126">{label}</span>
        </div>
      </div>;
  }
  return <div className="w-full max-w-md mx-auto" data-id="element-12127">
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-id="element-12128">
        {label && <div className="mb-3 text-center" data-id="element-12129">
            <span className="text-sm font-semibold text-slate-600" data-id="element-12130">
              {label}
            </span>
          </div>}
        <div className="flex flex-wrap gap-4 justify-center" data-id="element-12131">
          {showBills && breakdown.bills.map(bill => <div key={`bill-group-${bill.value}`} className={`flex ${groupBy === 'value' ? '-space-x-8 hover:space-x-1' : 'gap-2'} transition-all duration-300`} data-id="element-12132">
                {Array.from({
            length: Math.min(bill.count, 5)
          }).map((_, i) => <div key={`bill-${bill.value}-${i}`} className="relative hover:-translate-y-2 transition-transform duration-200" data-id="element-12133">
                    <MoneyToken cents={bill.value * 100} index={i} compact data-id="element-12134" />
                  </div>)}
              </div>)}
          {showCoins && breakdown.coins.map(coin => <div key={`coin-group-${coin.value}`} className={`flex ${groupBy === 'value' ? '-space-x-6 hover:space-x-1' : 'gap-2'} transition-all duration-300`} data-id="element-12135">
                {Array.from({
            length: Math.min(coin.count, 10)
          }).map((_, i) => <div key={`coin-${coin.value}-${i}`} className="relative hover:-translate-y-2 transition-transform duration-200" data-id="element-12136">
                    <MoneyToken cents={coin.value} index={i} compact data-id="element-12137" />
                  </div>)}
              </div>)}
        </div>
      </div>
    </div>;
}
// ── Support Mode ─────────────────────────────────────────────
// Full educational panel with breakdown, calculation, and tip.
// Shown after a wrong answer as scaffolding.
function SupportMode({
  safeAmount,
  actualBreakdown,
  showBills,
  showCoins,
  label
}: {
  safeAmount: number;
  actualBreakdown: MoneyBreakdown;
  showBills: boolean;
  showCoins: boolean;
  label?: string;
}) {
  return <div className="w-full max-w-lg mx-auto" data-id="element-12138">
      {/* Amount header */}
      <div className="bg-white rounded-xl p-5 mb-4 border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-12139">
        <span className="text-sm font-semibold text-slate-600" data-id="element-12140">
          {label || 'Totaal bedrag:'}
        </span>
        <span className="text-2xl font-black text-slate-800" data-id="element-12141">
          {formatMoney(safeAmount)}
        </span>
      </div>

      {/* Breakdown sections */}
      <div className="space-y-4" data-id="element-12142">
        {showBills && actualBreakdown.bills.length > 0 && <BreakdownSection title="Biljetten" icon={<Banknote className="w-5 h-5 text-slate-500" data-id="element-12144" />} items={actualBreakdown.bills.map(bill => ({
        cents: bill.value * 100,
        count: bill.count,
        displayValue: `€${bill.value}`,
        totalDisplay: `€${bill.count * bill.value}`
      }))} startDelay={0} data-id="element-12143" />}

        {showCoins && actualBreakdown.coins.length > 0 && <BreakdownSection title="Munten" icon={<Coins className="w-5 h-5 text-slate-500" data-id="element-12146" />} items={actualBreakdown.coins.map(coin => ({
        cents: coin.value,
        count: coin.count,
        displayValue: getDenominationDisplay(coin.value),
        totalDisplay: formatMoney(coin.count * coin.value)
      }))} startDelay={actualBreakdown.bills.length} data-id="element-12145" />}
      </div>

      {/* Educational tip */}
      <div className="mt-4 bg-slate-50 rounded-xl p-4 border border-slate-200 flex items-start gap-3" data-id="element-12147">
        <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-12148" />
        <div data-id="element-12149">
          <h4 className="text-sm font-bold text-slate-800 mb-1" data-id="element-12150">Tip</h4>
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-12151">
            Kies eerst grote biljetten, daarna kleinere, en werk af met munten.
            Zo heb je meestal minder stuks nodig.
          </p>
        </div>
      </div>
    </div>;
}
// ── Breakdown Section ────────────────────────────────────────
interface BreakdownItem {
  cents: number;
  count: number;
  displayValue: string;
  totalDisplay: string;
}
function BreakdownSection({
  title,
  icon,
  items,
  startDelay
}: {
  title: string;
  icon: React.ReactNode;
  items: BreakdownItem[];
  startDelay: number;
}) {
  return <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-id="element-12152">
      <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2" data-id="element-12153">
        {icon}
        <span data-id="element-12154">{title}</span>
      </h4>

      <div className="space-y-4" data-id="element-12155">
        {items.map((item, idx) => {
        const showCount = Math.min(item.count, isBill(item.cents) ? 5 : 10);
        return <motion.div key={`${title}-${item.cents}`} initial={{
          opacity: 0,
          x: -15
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: (startDelay + idx) * 0.08,
          duration: 0.3
        }} className="flex items-center gap-4" data-id="element-12156">
              <div className="flex gap-1.5 flex-wrap" data-id="element-12157">
                {Array.from({
              length: showCount
            }).map((_, i) => <MoneyToken key={i} cents={item.cents} index={i} compact data-id="element-12158" />)}
                {item.count > showCount && <div className="flex items-center justify-center px-2 py-1 text-slate-600 font-bold text-xs bg-slate-100 rounded-lg border border-slate-200" data-id="element-12159">
                    +{item.count - showCount}
                  </div>}
              </div>

              <div className="flex-1 text-right" data-id="element-12160">
                <span className="text-sm text-slate-500 font-medium" data-id="element-12161">
                  {item.count} × {item.displayValue} ={' '}
                  <strong className="text-slate-800 font-bold text-base" data-id="element-12162">
                    {item.totalDisplay}
                  </strong>
                </span>
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
}
// ── Main Component ───────────────────────────────────────────
export function MoneyVisual({
  amount,
  breakdown,
  showCoins = true,
  showBills = true,
  interactive = false,
  // Kept for prop compatibility, but not used visually here
  label,
  mode = 'support',
  groupBy = 'value'
}: MoneyVisualProps) {
  const safeAmount = useMemo(() => normalizeCents(amount), [amount]);
  const explicitBreakdown = useMemo(() => normalizeBreakdown(breakdown), [breakdown]);
  // Support mode uses explicit breakdown if provided, otherwise auto-generates
  const supportBreakdown = useMemo(() => explicitBreakdown ?? autoBreakdown(safeAmount), [explicitBreakdown, safeAmount]);
  if (mode === 'stimulus') {
    return <StimulusMode breakdown={explicitBreakdown} showBills={showBills} showCoins={showCoins} label={label} groupBy={groupBy} data-id="element-12163" />;
  }
  return <SupportMode safeAmount={safeAmount} actualBreakdown={supportBreakdown} showBills={showBills} showCoins={showCoins} label={label} data-id="element-12164" />;
}